import { useState } from "react";
import { CalendarClock } from "lucide-react";
import { SlideOver } from "@/components/ui/slide-over";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateTimeField } from "@/components/ui/datetime-field";
import { Notify } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { ConfidenceChip, type ConfidenceDisplay } from "@/features/simulations/ConfidenceChip";
import {
  METRIC_LABELS,
  METRIC_ORDER,
  type AppliedScenarioMetricKey,
} from "@/types/applied-scenario.types";
import { useApplyScenario } from "./hooks/useAppliedScenario";

interface ApplyScenarioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lineId: string;
  simulationId: string;
  simulatedHours: number;
  // Spec 079 — o nível E a base viajam juntos: o chip declara qual das duas produziu o número.
  winnerConfidence: ConfidenceDisplay;
  baselineConfidence: ConfidenceDisplay;
  onApplied?: (id: string) => void;
}

// Promover um cenário a COMPROMISSO. O gestor declara três coisas e só três:
// quando começa, por quê, e qual faixa de desvio ele aceita.
// O FIM não é campo: é derivado pelo servidor consumindo o expediente da linha. Exibi-lo
// como campo editável convidaria a declarar uma vigência de 8 h numa linha que abre 4 h/dia — e a
// apuração compararia metade do trabalho com a previsão inteira.
// As tolerâncias são declaradas AQUI, antes de ver o resultado. Preenchê-las na apuração
// transformaria "ficou na faixa" em desculpa escolhida depois do fato.

export function ApplyScenarioDialog({
  open,
  onOpenChange,
  lineId,
  simulationId,
  simulatedHours,
  winnerConfidence,
  baselineConfidence,
  onApplied,
}: ApplyScenarioDialogProps) {
  const apply = useApplyScenario();
  const [windowStart, setWindowStart] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [tolerances, setTolerances] = useState<Partial<Record<AppliedScenarioMetricKey, number>>>(
    {}
  );
  // O LIMIAR de aderência. O formulário SUGERE 90; o servidor não sugere nada.
  // A sugestão é do formulário de propósito: um limiar que o servidor preenchesse sozinho não teria
  // sido declarado por ninguém, e o gate de acurácia perderia a razão de existir.
  const [adherenceThresholdPct, setAdherenceThresholdPct] = useState<number | undefined>(90);

  const thresholdValid =
    adherenceThresholdPct !== undefined &&
    adherenceThresholdPct >= 0 &&
    adherenceThresholdPct <= 100;
  const canSubmit = !!windowStart && reason.trim().length > 0 && thresholdValid && !apply.isPending;

  function submit() {
    if (!windowStart || adherenceThresholdPct === undefined) return;
    apply.mutate(
      {
        lineId,
        simulationId,
        windowStart,
        reason: reason.trim(),
        adherenceThresholdPct,
        tolerances: METRIC_ORDER.filter((m) => (tolerances[m] ?? 0) > 0).map((metric) => ({
          metric,
          tolerancePct: tolerances[metric] as number,
        })),
      },
      {
        onSuccess: (applied) => {
          onOpenChange(false);
          onApplied?.(applied.id);
        },
      }
    );
  }

  return (
    <SlideOver open={open} onOpenChange={onOpenChange} title="Aplicar cenário" width={520}>
      <div className="space-y-5 overflow-y-auto p-5">
        {/* Princípio II — a confiança das duas execuções lado a lado, ANTES de comprometer. */}
        <div className="flex items-center justify-between gap-3 rounded-md border border-border-subtle px-3 py-2">
          <span className="flex flex-col gap-1 text-[12px] text-text-secondary">
            Cenário escolhido
            <ConfidenceChip {...winnerConfidence} />
          </span>
          <span className="flex flex-col gap-1 text-[12px] text-text-secondary">
            Não fazer nada (base)
            <ConfidenceChip {...baselineConfidence} />
          </span>
        </div>

        <div className="space-y-1.5">
          <DateTimeField
            id="window-start"
            label="Início da vigência"
            value={windowStart}
            onChange={setWindowStart}
            minIso={new Date().toISOString()}
          />
          <p className="flex items-start gap-1.5 text-[12px] text-text-secondary">
            {/* ⚠️ NÃO afirme "consome {simulatedHours} h de expediente". O servidor consome
                `Σ batch.operatingWindows` — o expediente COLAPSADO pelo calendário —, e
                `horizonHours` é o horizonte de RELÓGIO. Numa linha de 8 h/dia com horizonte de
                24 h os dois diferem 3×, e o número seria falso exatamente na tela em que a
                fábrica é comprometida. O expediente efetivo não viaja no contrato do lote. */}
            <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />O fim é
            calculado pelo servidor, consumindo o mesmo expediente desta linha que a rodada de{" "}
            {simulatedHours} h simulou — não {simulatedHours} h de relógio. Numa linha que não abre
            24 h, a vigência atravessa mais de um dia.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="reason">Por que este cenário</Label>
          <Input
            id="reason"
            value={reason}
            maxLength={500}
            placeholder="Ex.: priorizar a OP do cliente A sem estourar o prazo"
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="adherence-threshold">Limiar de aderência</Label>
          <div className="flex items-center gap-1.5">
            <Input
              id="adherence-threshold"
              type="number"
              min={0}
              max={100}
              className="h-8 w-24 text-right"
              placeholder="90"
              value={adherenceThresholdPct ?? ""}
              onChange={(e) =>
                setAdherenceThresholdPct(e.target.value === "" ? undefined : Number(e.target.value))
              }
            />
            <span className="text-[12px] text-text-tertiary">%</span>
          </div>
          <p className="text-[12px] text-text-secondary">
            Quanto do combinado a operação precisa cumprir para que esta decisão conte na
            estatística de acerto do gêmeo. Declarado agora, antes de existir resultado para olhar.
          </p>
        </div>

        {/* A ordem e o CONJUNTO das métricas vêm do contrato (`METRIC_ORDER`), nunca de uma cópia
            local: é esta lista que decide para quais métricas o gestor pode declarar faixa, e
            `settle.ts` grava uma linha por métrica do MESMO `METRIC_ORDER`. Duplicá-la faria a
            próxima métrica nascer apurada e para sempre sem veredito, porque o formulário nunca
            chegou a oferecer a faixa dela. */}
        <fieldset className="space-y-2">
          <legend className="text-[13px] font-medium">Tolerância por métrica (opcional)</legend>
          <p className="text-[12px] text-text-secondary">
            Faixa de desvio aceitável em <strong className="font-medium">% do previsto</strong>, não
            em pontos percentuais: 5% sobre uma Disponibilidade prevista de 80 aceita de 76 a 84.
            Métrica sem faixa declarada é apurada sem veredito — em branco é honesto, zero não
            seria.
          </p>
          {METRIC_ORDER.map((metric) => (
            <div key={metric} className="flex items-center justify-between gap-3">
              {/* ⚠️ NADA de "(pp)" aqui. `evaluateTolerance` compara `|realizado − previsto| /
                  |previsto|`, isto é, % RELATIVO ao previsto — 5 numa Disponibilidade prevista de
                  80 pp aceita ±4 pp, não ±5 pp. O sufixo "%" ao lado do campo já é a unidade certa;
                  rotular de ponto percentual prometia outra faixa e a apuração reprovava dentro do
                  que o gestor achou ter declarado. */}
              <Label htmlFor={`tol-${metric}`} className="text-[13px] font-normal">
                {METRIC_LABELS[metric]}
              </Label>
              <div className="flex items-center gap-1.5">
                <Input
                  id={`tol-${metric}`}
                  type="number"
                  min={0}
                  className="h-8 w-24 text-right"
                  placeholder="0"
                  value={tolerances[metric] ?? ""}
                  onChange={(e) =>
                    setTolerances((prev) => ({
                      ...prev,
                      [metric]: e.target.value === "" ? undefined : Number(e.target.value),
                    }))
                  }
                />
                <span className="text-[12px] text-text-tertiary">%</span>
              </div>
            </div>
          ))}
        </fieldset>

        {apply.isError ? (
          <Notify.Banner variant="danger" title="Não foi possível aplicar">
            {getFriendlyErrorMessage(apply.error)}
          </Notify.Banner>
        ) : null}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {apply.isPending ? "Aplicando…" : "Aplicar cenário"}
          </Button>
        </div>
      </div>
    </SlideOver>
  );
}
